import { getPlayerHomePath } from '@config/releaseProfile';
import { User, UserRole } from '@types/index';
import { isSafeAppReturnUrl } from '@utils/safeReturnUrl';
import { mapPublicContestUrlToPortal } from '@utils/contestSubmitNavigation';

type RoleLike = User['role'] | string | undefined;

export function isSuperAdminUser(user: Pick<User, 'role'> & { isSuperAdmin?: boolean } | null | undefined): boolean {
  if (!user) return false;
  const role = String(user.role || '');
  if (role === UserRole.ACADEMY_ADMIN || role === 'academy_admin') return false;
  if (user.isSuperAdmin === true) return true;
  return role === UserRole.SUPER_ADMIN || role === 'super_admin';
}

export function isPlatformAdminUser(user: Pick<User, 'role'> & { isSuperAdmin?: boolean } | null | undefined): boolean {
  if (!user) return false;
  if (isSuperAdminUser(user)) return true;
  const role = String(user.role || '');
  return (
    role === UserRole.ADMIN ||
    role === 'admin' ||
    role === UserRole.EVALUATION_ADMIN ||
    role === 'evaluation_admin' ||
    role === UserRole.CONTEST_ADMIN ||
    role === 'contest_admin'
  );
}

/** Default landing path after login (or when an authenticated user hits /login). */
export function getPostLoginPath(user: Pick<User, 'role'> & { isSuperAdmin?: boolean } | null | undefined): string {
  if (!user) return '/login';

  const role = String(user.role || '') as RoleLike;

  if (isSuperAdminUser(user) || role === UserRole.ADMIN || role === 'admin') {
    return '/admin';
  }
  if (role === UserRole.EVALUATION_ADMIN || role === 'evaluation_admin') {
    return '/admin';
  }
  if (role === UserRole.CONTEST_ADMIN || role === 'contest_admin') {
    return '/admin?view=contest-tasks';
  }
  if (role === UserRole.ACADEMY_ADMIN || role === 'academy_admin') {
    return '/academy-admin/dashboard';
  }
  if (role === UserRole.SPONSOR || role === 'sponsor') {
    return '/sponsor/dashboard';
  }
  if (role === UserRole.COACH || role === 'coach') {
    return '/coach';
  }
  if (role === UserRole.PLAYER || role === 'player') {
    return getPlayerHomePath();
  }
  return '/dashboard';
}

/**
 * returnUrl is honoured for players/coaches; platform admins only when target is /admin*.
 */
export function resolvePostLoginDestination(
  user: Pick<User, 'role'> & { isSuperAdmin?: boolean } | null | undefined,
  returnUrl: string | null | undefined
): string {
  if (isSafeAppReturnUrl(returnUrl)) {
    if (isPlatformAdminUser(user)) {
      if (returnUrl.startsWith('/admin')) return returnUrl;
    } else {
      if (returnUrl.startsWith('/contests/')) {
        return mapPublicContestUrlToPortal(user, returnUrl);
      }
      const role = String(user?.role || '');
      if (
        (role === UserRole.COACH || role === 'coach') &&
        returnUrl.startsWith('/player/contests/')
      ) {
        return returnUrl.replace('/player/contests/', '/coach/contests/');
      }
      return returnUrl;
    }
  }
  return getPostLoginPath(user);
}

/** Merge extra query params onto an in-app path that may already have a query string. */
export function withAppQuery(
  path: string,
  extras: Record<string, string | null | undefined>,
): string {
  const qIndex = path.indexOf('?');
  const pathname = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const params = new URLSearchParams(qIndex >= 0 ? path.slice(qIndex + 1) : '');
  Object.entries(extras).forEach(([key, value]) => {
    const trimmed = String(value || '').trim();
    if (trimmed) params.set(key, trimmed);
  });
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/** Check-email screen after register when verification is required. */
export function buildCheckEmailPath(options: {
  email?: string | null;
  role?: string | null;
  returnUrl?: string | null;
} = {}): string {
  return withAppQuery('/verify-email', {
    pending: '1',
    email: options.email || undefined,
    role: options.role || undefined,
    returnUrl: isSafeAppReturnUrl(options.returnUrl) ? options.returnUrl : undefined,
  });
}

/**
 * Sign-in URL after register (or when bouncing from a gated page).
 * `registered` is a short code the login page maps to a durable Alert.
 */
export function buildSignInPath(options: {
  returnUrl?: string | null;
  registered?: string | null;
  email?: string | null;
  fresh?: boolean | null;
} = {}): string {
  return withAppQuery('/login', {
    returnUrl: isSafeAppReturnUrl(options.returnUrl) ? options.returnUrl : undefined,
    registered: options.registered || undefined,
    email: options.email || undefined,
    fresh: options.fresh ? '1' : undefined,
  });
}

/** After email verify, stay on /login even if another user is already in this browser. */
export function shouldStayOnLoginForFreshSignIn(searchParams: {
  get: (key: string) => string | null;
}): boolean {
  return searchParams.get('fresh') === '1' || searchParams.get('registered') === 'verified';
}

export function isFamilyOnboardingRole(
  user: Pick<User, 'role'> | null | undefined,
): boolean {
  const role = String(user?.role || '');
  return role === UserRole.PLAYER || role === 'player' || role === UserRole.COACH || role === 'coach';
}

export function needsFamilyOnboarding(
  user: Pick<User, 'needsFamilyOnboarding' | 'isChildInCustody' | 'role'> | null | undefined,
): boolean {
  if (!user?.needsFamilyOnboarding || user.isChildInCustody === true) return false;
  return isFamilyOnboardingRole(user);
}

export function buildFamilySetupPath(returnUrl?: string | null): string {
  return withAppQuery('/family-setup', {
    returnUrl: isSafeAppReturnUrl(returnUrl) ? returnUrl : undefined,
  });
}

/**
 * After family status is saved, go to profile — not contests —
 * so a married parent can add a child. Individuals stay on Profile.
 */
export function getFamilySetupNextPath(
  user: Pick<User, 'role' | 'canCreateChild' | 'maritalStatus' | 'isChildInCustody'> | null | undefined,
): string {
  if (user?.isChildInCustody) return getPostLoginPath(user);
  const role = String(user?.role || '');
  if (role === UserRole.COACH || role === 'coach') {
    return '/coach/profile';
  }
  if (user?.canCreateChild && user?.maritalStatus !== 'individual') {
    return '/player/profile?tab=children';
  }
  return '/player/profile';
}

/** Copy shown on /login after a successful register (no toast-only hop). */
export function registeredSignInNotice(
  registered: string | null | undefined,
  returnUrl?: string | null,
): string | null {
  const code = String(registered || '').trim().toLowerCase();
  if (!code) return null;
  if (code === 'verify') {
    return 'Check your email to verify, then sign in with your email and password.';
  }
  if (code === 'verified') {
    return 'Email confirmed. Sign in to continue.';
  }
  if (code === 'consent') {
    return 'Account created. Confirm the verification email, then sign in.';
  }
  if (code === 'academy') {
    return 'Account created. Sign in with your Admin ID and the password you set — not the contact email.';
  }
  if (code === 'child' || (returnUrl && returnUrl.includes('addChild'))) {
    return 'Account created. Sign in with your email and password, then add your child from Profile.';
  }
  return 'Account created. Sign in with your email and password.';
}
